<template>
  <AppDialogContent
    v-model="dialog"
    max-width="825px"
    :error="formError"
    :persistent="disabled"
    :loading="disabled"
    @submit-form="override($event)"
  >
    <template #header>
      {{
        $t(
          "people:PersonProfile.qualifications.qualificationsOverrideForm.title",
          "Override qualifications"
        )
      }}
    </template>

    <v-form :disabled="disabled">
      <AppSelectDiscipline
        v-model="form.disciplineId"
        :input-props="{
          disabled: editMode,
          clearable: false
        }"
      />
      <AppSelectQualificationGroup
        v-model="form.qualificationGroupId"
        :input-props="{
          disabled: editMode || disciplineIds.length == 0,
          clearable: false
        }"
        :discipline-ids="disciplineIds"
        :ranked-entities="rankedEntities"
      />
      <AppSelectPerson
        :key="dialog + '-person'"
        v-model="form.personId"
        :input-props="{
          label: $t(
            'people:PersonProfile.qualifications.qualificationsOverrideForm.input.person.label',
            'Person'
          ),
          disabled: cannotEditPersonInput
        }"
      />
      <AppSelectHorse
        :key="dialog + '-horse'"
        v-model="form.horseId"
        :input-props="{
          label: $t(
            'people:PersonProfile.qualifications.qualificationsOverrideForm.input.horse.label',
            'Horse'
          ),
          disabled: cannotEditHorseInput
        }"
      />

      <div class="my-2">
        <table v-if="showQualifications" class="entsys--table">
          <thead>
            <tr>
              <th />
              <th>
                {{
                  $t(
                    "people:PersonProfile.qualifications.qualificationsOverrideForm.qualificationsTable.currentState",
                    "Current"
                  )
                }}
              </th>
              <th id="qualificatioNoOverride">
                {{
                  $t(
                    "people:PersonProfile.qualifications.qualificationsOverrideForm.qualificationsTable.noOverride",
                    "No override"
                  )
                }}
              </th>
              <th id="qualificationQualified">
                {{
                  $t(
                    "people:PersonProfile.qualifications.qualificationsOverrideForm.qualificationsTable.overrideAsQualified",
                    "Override as qualified"
                  )
                }}
              </th>
              <th id="qualificationNotQualified">
                {{
                  $t(
                    "people:PersonProfile.qualifications.qualificationsOverrideForm.qualificationsTable.overrideAsUnqualified",
                    "Override as not qualified"
                  )
                }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="q in qualificationsByGroupId" :key="q.id">
              <td>{{ q.name }}</td>
              <td class="text-center">
                <AppShowQualificationStatus
                  :status="findCalculatedStatus(q)"
                  no-margin
                />
              </td>
              <td class="text-center">
                <v-radio-group
                  v-model="form.selectedStatuses[q.id]"
                  hide-details
                  class="pt-0 mt-0"
                >
                  <v-radio
                    aria-labelledby="qualificatioNoOverride"
                    :name="'override-' + q.id"
                    :value="null"
                    class="mr-0 justify-center"
                  />
                </v-radio-group>
              </td>
              <td class="text-center">
                <v-radio-group
                  v-model="form.selectedStatuses[q.id]"
                  hide-details
                  class="pt-0 mt-0"
                >
                  <v-radio
                    :name="'override-' + q.id"
                    aria-labelledby="qualificationQualified"
                    value="QUALIFIED_MANUALLY"
                    class="mr-0 justify-center"
                  />
                </v-radio-group>
              </td>
              <td class="text-center">
                <v-radio-group
                  v-model="form.selectedStatuses[q.id]"
                  hide-details
                  class="pt-0 mt-0"
                >
                  <v-radio
                    :name="'override-' + q.id"
                    aria-labelledby="qualificationNotQualified"
                    value="UNQUALIFIED_MANUALLY"
                    class="mr-0 justify-center"
                  />
                </v-radio-group>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-form>
  </AppDialogContent>
</template>
<script>
import { ref, watch, computed } from "vue";
import gql from "fraql";
import { useQualificationGroupHelper } from "@composables/masterdata/qualifications/qualificationGroups";
import { useQualificationHelper } from "@composables/masterdata/qualifications/qualifications";
import { useSaveMeta } from "@/composables/form/save_meta";
import { useFormValidation } from "@/composables/form/form_validation";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppSelectDiscipline from "@AppSelects/AppSelectDiscipline.vue";
import AppSelectQualificationGroup from "@AppSelects/AppSelectQualificationGroup.vue";
import AppSelectPerson from "@AppSelects/AppSelectPerson.vue";
import AppSelectHorse from "@AppSelects/AppSelectHorse.vue";
import AppShowQualificationStatus from "@AppShows/AppShowQualificationStatus.vue";

export default {
  name: "QualificationOverrideForm",
  components: {
    AppDialogContent,
    AppSelectDiscipline,
    AppSelectQualificationGroup,
    AppSelectPerson,
    AppSelectHorse,
    AppShowQualificationStatus
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    personId: {
      // If given, means its opened on person profile
      type: String,
      default: null
    },
    horseId: {
      // If given, means its opened on horse profile
      type: String,
      default: null
    },
    qualificationStatuses: {
      // Server returned qualification statuses based on either personId or horseId parameter
      type: Array,
      required: true
    }
  },
  emits: ["update:modelValue"],
  setup(props) {
    const { masterQualificationGroups } = useQualificationGroupHelper();
    const { masterQualificationsActive, masterQualifications } =
      useQualificationHelper();

    const form = ref({
      personId: props.personId,
      horseId: props.horseId,
      qualificationGroupId: null,
      selectedStatuses: {}
    });

    const { saveMetaSetOriginalForm, saveMetaConfirmUnsaveChanges } =
      useSaveMeta(form, { usageInModal: true });

    const initForm = ref({
      vuelidateValidations: {},
      readOnlyFields: []
    });
    const { v$, fieldErrorMessages, fieldDisabled } = useFormValidation(
      initForm,
      form
    );

    watch(
      () => form.value.qualificationGroupId,
      () => {
        updateSelectValues();
      }
    );

    const isPairQualification = computed(() => {
      if (!form.value.qualificationGroupId) return false;
      return (
        masterQualificationGroups.value.find(
          x => x.id == form.value.qualificationGroupId
        )?.rankedEntity == "Pair"
      );
    });

    const qualificationsByGroupId = computed(() => {
      if (!form.value.qualificationGroupId) return [];
      return masterQualificationsActive.value
        .filter(q => q.qualificationGroupId == form.value.qualificationGroupId)
        .sort((a, b) => (a.rank == b.rank ? 0 : a.rank > b.rank ? 1 : -1));
    });

    const qualificationIds = computed(() => {
      return qualificationsByGroupId.value.map(x => x.id);
    });

    function updateSelectValues() {
      form.value.selectedStatuses = {};
      qualificationIds.value.forEach(qualificationId => {
        const qualificationStatus = props.qualificationStatuses.find(
          ({ qualification: q, horse: h, person: p }) =>
            q.id == qualificationId &&
            (h?.id ?? null) == form.value.horseId &&
            (p?.id ?? null) == form.value.personId
        );
        if (!qualificationStatus) {
          form.value.selectedStatuses[qualificationId] = null;
          return;
        }
        const val = ["QUALIFIED_MANUALLY", "UNQUALIFIED_MANUALLY"].includes(
          qualificationStatus.status
        )
          ? qualificationStatus.status
          : null;
        form.value.selectedStatuses[qualificationId] = val;
      });

      if (!isPairQualification.value) {
        // Reset manually selected person/horse if group's rankedEntity is not Pair
        if (!props.horseId) {
          form.value.horseId = null;
        }
        if (!props.personId) {
          form.value.personId = null;
        }
      }
    }

    return {
      form,
      masterQualificationGroups,
      masterQualifications,
      masterQualificationsActive,

      isPairQualification,
      qualificationsByGroupId,
      qualificationIds,
      updateSelectValues,
      saveMetaSetOriginalForm,
      saveMetaConfirmUnsaveChanges,

      v$,
      fieldErrorMessages,
      fieldDisabled
    };
  },
  data() {
    return {
      disabled: false,
      editMode: false,
      formError: {}
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      async set(v) {
        if (v == false) {
          const response = await this.saveMetaConfirmUnsaveChanges();
          if (!response) return;
        }
        this.$emit("update:modelValue", v);
      }
    },
    showQualifications() {
      if (this.form.disciplineId && this.form.qualificationGroupId) {
        if (this.isPairQualification == true) {
          return Boolean(this.form.horseId && this.form.personId);
        }
        return true;
      }
      return false;
    },
    cannotEditPersonInput() {
      return (
        this.editMode ||
        Boolean(this.personId) || // If personId given, means opened in its profile. Same logic with horseId
        !this.isPairQualification // If rankedEntity is Pair, then allow
      );
    },
    cannotEditHorseInput() {
      return (
        this.editMode || Boolean(this.horseId) || !this.isPairQualification
      );
    },
    disciplineIds() {
      return this.form.disciplineId ? [this.form.disciplineId] : [];
    },
    rankedEntities() {
      if (this.personId) {
        return ["Pair", "Person"];
      }
      if (this.horseId) {
        return ["Pair", "Horse"];
      }
      return [];
    }
  },
  methods: {
    setForm(item, editMode = false) {
      this.editMode = editMode ?? false;

      this.form = {
        qualificationGroupId: null,
        disicplineId: null,
        personId: null,
        horseId: null,
        selectedStatuses: {},
        ...item
      };
      this.v$.form.$reset();

      this.updateSelectValues();
      this.saveMetaSetOriginalForm(this.form);
    },
    async override() {
      this.disabled = true;
      this.formError = {};
      try {
        const qualificationIds = Object.entries(
          this.form.selectedStatuses
        ).reduce(
          (obj, [qualificationId, status]) => {
            if (status == "QUALIFIED_MANUALLY")
              obj.qualified.push(qualificationId);
            else if (status == "UNQUALIFIED_MANUALLY")
              obj.disqualified.push(qualificationId);
            return obj;
          },
          { qualified: [], disqualified: [] }
        );
        await this.$apollo.mutate({
          mutation: gql`
            mutation qualificationGroupUpdateOverrides(
              $qualificationGroupId: ID!
              $personId: ID
              $horseId: ID
              $qualifiedQualificationIds: [ID!]!
              $unqualifiedQualificationIds: [ID!]!
            ) {
              qualificationGroupUpdateOverrides(
                input: {
                  qualificationGroupId: $qualificationGroupId
                  personId: $personId
                  horseId: $horseId
                  qualifiedQualificationIds: $qualifiedQualificationIds
                  unqualifiedQualificationIds: $unqualifiedQualificationIds
                }
              ) {
                success
              }
            }
          `,
          variables: {
            qualificationGroupId: this.form.qualificationGroupId,
            personId: this.personId || this.form.personId,
            horseId: this.horseId || this.form.horseId,
            qualifiedQualificationIds: qualificationIds.qualified,
            unqualifiedQualificationIds: qualificationIds.disqualified
          },
          refetchQueries: ["qualificationStatuses"],
          awaitRefetchQueries: true
        });
        this.saveMetaSetOriginalForm(this.form);
        this.dialog = false;
      } catch (error) {
        this.formError = { type: "graphql", err: error };
        this.$root.$snackbarServerError(error, { level: "error" });
      }
      this.disabled = false;
    },
    findCalculatedStatus(qualification) {
      const statusFound = this.qualificationStatuses.find(
        ({ qualification: q, horse: h, person: p }) =>
          q.id == qualification.id &&
          (h?.id ?? null) == this.form.horseId &&
          (p?.id ?? null) == this.form.personId
      );
      if (statusFound) return statusFound.status;
      else return "unknown";
    }
  }
};
</script>
